import { ContextualAiChoice } from './contextualAiTypes';

export type TemplateDiscoveryTopic =
    | 'outcomes'
    | 'abilities'
    | 'autonomy'
    | 'risks-and-prevention'
    | 'positive-practices'
    | 'collaboration'
    | 'safety-compliance-ethics'
    | 'performance-contrast';

export interface TemplateDiscoveryContext {
    jobDescription: string;
    knownContext?: string;
    answeredTopics?: TemplateDiscoveryTopic[];
}

export interface TemplateDiscoveryQuestion {
    topic: TemplateDiscoveryTopic;
    prompt: string;
    choices: ContextualAiChoice[];
    allowFreeText: true;
}

const mentions = (text: string, terms: string[]) => terms.some(term => text.includes(term));

/**
 * Produces a small, high-value interview from information missing in the role
 * description. Risk prompts always ask for the preventing positive practice in
 * the same turn, so discovery cannot drift into a punitive list.
 */
export const buildTemplateDiscoveryInterview = ({
    jobDescription,
    knownContext = '',
    answeredTopics = [],
}: TemplateDiscoveryContext): TemplateDiscoveryQuestion[] => {
    const source = `${jobDescription} ${knownContext}`.toLowerCase();
    const answered = new Set(answeredTopics);
    const candidates: Array<TemplateDiscoveryQuestion & { covered: boolean }> = [
        {
            topic: 'outcomes',
            prompt: 'What are the two or three outcomes that matter most in this role?',
            choices: [{ id: 'quality', label: 'Quality' }, { id: 'speed', label: 'Speed' }, { id: 'service', label: 'Service' }],
            allowFreeText: true,
            covered: mentions(source, ['outcome', 'result', 'deliver', 'goal', 'responsib']),
        },
        {
            topic: 'abilities',
            prompt: 'Which abilities must someone already be able to demonstrate to succeed?',
            choices: [{ id: 'technical', label: 'Technical judgment' }, { id: 'analysis', label: 'Analysis' }, { id: 'communication', label: 'Communication' }],
            allowFreeText: true,
            covered: mentions(source, ['skill', 'ability', 'proficien', 'experience', 'knowledge']),
        },
        {
            topic: 'autonomy',
            prompt: 'Which decisions can this person make independently, and when must they escalate?',
            choices: [{ id: 'guided', label: 'Mostly guided' }, { id: 'independent', label: 'Independent' }, { id: 'leader', label: 'Sets direction' }],
            allowFreeText: true,
            covered: mentions(source, ['decision', 'autonom', 'authority', 'independ', 'escalat', 'supervis']),
        },
        {
            topic: 'risks-and-prevention',
            prompt: 'Where could performance break down in a way that matters, and what support or observable positive practice helps prevent it?',
            choices: [{ id: 'safety', label: 'Safety failure' }, { id: 'quality', label: 'Quality failure' }, { id: 'trust', label: 'Customer or team trust' }],
            allowFreeText: true,
            covered: mentions(source, ['risk', 'failure', 'prevent', 'hazard', 'error', 'incident']),
        },
        {
            topic: 'positive-practices',
            prompt: 'What does great performance look like here—what do your strongest people consistently do that others can learn and practice?',
            choices: [{ id: 'plan', label: 'Plan ahead' }, { id: 'communicate', label: 'Communicate early' }, { id: 'coach', label: 'Coach others' }],
            allowFreeText: true,
            covered: mentions(source, ['best practice', 'consistently', 'proactive', 'coach', 'communicat']),
        },
        {
            topic: 'collaboration',
            prompt: 'Who does this role work with most, and what does effective collaboration look like?',
            choices: [{ id: 'customers', label: 'Customers' }, { id: 'peers', label: 'Peers' }, { id: 'leaders', label: 'Leaders' }],
            allowFreeText: true,
            covered: mentions(source, ['customer', 'stakeholder', 'team', 'collaborat', 'partner', 'audience']),
        },
        {
            topic: 'safety-compliance-ethics',
            prompt: 'Are there safety, compliance, privacy, or ethical boundaries this person must protect?',
            choices: [{ id: 'safety', label: 'Safety' }, { id: 'compliance', label: 'Compliance' }, { id: 'privacy', label: 'Privacy or ethics' }, { id: 'none', label: 'No special boundary' }],
            allowFreeText: true,
            covered: mentions(source, ['safety', 'compliance', 'regulat', 'privacy', 'ethical', 'policy']),
        },
        {
            topic: 'performance-contrast',
            prompt: 'What separates strong performance from merely adequate performance in this role?',
            choices: [{ id: 'ownership', label: 'Ownership' }, { id: 'judgment', label: 'Judgment' }, { id: 'consistency', label: 'Consistency' }],
            allowFreeText: true,
            covered: mentions(source, ['strong performance', 'exceed', 'excellent', 'adequate', 'standard']),
        },
    ];

    const missing = candidates.filter(question => !answered.has(question.topic) && !question.covered);
    // Three to seven turns is the normal interview shape, but confirmed context
    // always wins over that heuristic. Re-asking a known question only to reach
    // a target count makes the conversation feel mechanical and wastes the
    // user's time.
    return missing
        .slice(0, 7)
        .map(({ covered: _covered, ...question }) => question);
};
